/**
 * Client helpers for board pendingDigest + constraints ledger.
 */

import { postBoardAiDigestEvent } from '../api/boardAiApi';
import { useBoardStore } from '../store/useBoardStore';
import { pruneFillsForDeletedSources } from './boardContextRecommendations';
import {
  markDigestDeleted,
  withdrawConstraintsForDeletedSources,
} from './withdrawConstraints';
import { collectTextAboutLinks } from './textAboutObject';
import { clusterForPinShape } from './frameClusterDigest';

/** Match server OBJECT_TEXT_CAP / DIGEST_EXCERPT_MAX — capped raw text or AI summary. */
const DIGEST_EXCERPT_MAX = 600;

function capDigestExcerpt(text, max = DIGEST_EXCERPT_MAX) {
  const s = String(text || '').trim().replace(/\s+/g, ' ');
  if (!s) return '';
  if (s.length <= max) return s;
  const head = Math.max(40, Math.floor(max * 0.7));
  const tail = Math.max(20, max - head - 1);
  return `${s.slice(0, head)}…${s.slice(-tail)}`;
}

/**
 * Prefer capped raw pin text when under DIGEST_EXCERPT_MAX.
 * When the dump is longer, use intentSummary (AI compressed at enrich time).
 */
function digestEvidenceText({
  intentSummary = '',
  note = '',
  selectionText = '',
  title = '',
  fallback = '',
} = {}) {
  const summary = String(intentSummary || '').trim();
  const raw = [note, selectionText].filter(Boolean).join('\n').trim()
    || String(title || '').trim()
    || String(fallback || '').trim();
  if (raw && raw.length <= DIGEST_EXCERPT_MAX) {
    return capDigestExcerpt(raw);
  }
  if (summary) return capDigestExcerpt(summary);
  if (raw) return capDigestExcerpt(raw);
  return '';
}

function clusterForActivity(editor, activityId) {
  if (!editor?.getCurrentPageShapes || !activityId) return null;
  const needle = String(activityId);
  for (const shape of editor.getCurrentPageShapes() || []) {
    if (shape?.type !== 'pin-card') continue;
    if (String(shape.meta?.activityId || '') !== needle) continue;
    return clusterForPinShape(editor, shape);
  }
  return null;
}

export function buildDigestAddEvents({
  activityIds = [],
  aiPins = [],
  canvasEvidences = [],
  activities = [],
  editor = null,
  relations = [],
} = {}) {
  const byId = new Map(
    (activities || []).map(a => [String(a?._id || a?.id || a?.activityId || ''), a])
  );
  const events = [];
  const at = Date.now();
  const aboutByTextId = new Map(
    (editor ? collectTextAboutLinks(editor) : []).map(link => [link.textId, link])
  );

  for (const id of activityIds) {
    const activityId = String(id || '').trim();
    if (!activityId) continue;
    const activity = byId.get(activityId);
    const comment = activity?.action || activity?.comment || {};
    const highlight = comment?.highlightData || {};
    const content = activity?.content?.[0] || {};
    const intentSummary = digestEvidenceText({
      intentSummary: highlight.intentSummary,
      note: comment.text || comment.body || '',
      selectionText: highlight.selectionText || highlight.ocrText || '',
      title: content.title || '',
    });
    const label = String(content.title || intentSummary || activityId).trim().slice(0, 80);
    const cluster = clusterForActivity(editor, activityId);
    events.push({
      op: 'add',
      pinId: activityId,
      label,
      intentSummary,
      ...(cluster?.frameId ? { frameId: cluster.frameId } : {}),
      ...(cluster?.clusterLabel ? { clusterLabel: cluster.clusterLabel } : {}),
      at,
    });
  }

  for (const pin of aiPins) {
    if (!pin) continue;
    const shapeId = String(pin.id || pin.shapeId || '').trim();
    const pinId = String(pin.activityId || '').trim();
    if (!shapeId && !pinId) continue;
    const intentSummary = digestEvidenceText({
      intentSummary: pin.intentSummary || '',
      note: '',
      selectionText: pin.intentText || pin.text || '',
      title: pin.title || '',
      fallback: pin.query || '',
    });
    const liveShape = editor && shapeId ? editor.getShape?.(shapeId) : null;
    const cluster = liveShape ? clusterForPinShape(editor, liveShape) : null;
    events.push({
      op: 'add',
      pinId,
      shapeId,
      label: String(pin.title || intentSummary || shapeId || pinId).trim().slice(0, 80),
      intentSummary,
      ...(cluster?.frameId ? { frameId: cluster.frameId } : {}),
      ...(cluster?.clusterLabel ? { clusterLabel: cluster.clusterLabel } : {}),
      at,
    });
  }

  for (const evidence of canvasEvidences) {
    if (!evidence) continue;
    const shapeId = String(evidence.shapeId || '').trim();
    if (!shapeId) continue;
    const intentSummary = digestEvidenceText({
      intentSummary: evidence.intentSummary || '',
      note: '',
      selectionText: evidence.text || '',
      title: '',
    });
    const about = aboutByTextId.get(shapeId);
    const aboutKind = String(evidence.aboutKind || about?.aboutKind || '').trim();
    const aboutPinId = String(
      evidence.aboutPinId || (aboutKind === 'pin' ? about?.aboutId : '')
    ).trim();
    const aboutShapeId = String(
      evidence.aboutShapeId
      || (about && aboutKind && aboutKind !== 'pin' ? about.aboutId : '')
    ).trim();
    events.push({
      op: 'add',
      shapeId,
      label: intentSummary.slice(0, 80) || shapeId,
      intentSummary,
      ...(aboutPinId ? { aboutPinId } : {}),
      ...(aboutShapeId ? { aboutShapeId } : {}),
      ...(aboutKind ? { aboutKind } : {}),
      ...(evidence.fromId ? { fromId: String(evidence.fromId) } : {}),
      ...(evidence.toId ? { toId: String(evidence.toId) } : {}),
      ...(evidence.relationLabel ? { relationLabel: String(evidence.relationLabel) } : {}),
      source: evidence.source || 'clipboard-paste',
      aiEnriched: Boolean(evidence.aiEnriched),
      at,
    });
  }

  for (const relation of relations || []) {
    if (!relation?.shapeId || !relation.fromId || !relation.toId || !relation.relationLabel) {
      continue;
    }
    events.push({
      op: 'add',
      shapeId: String(relation.shapeId),
      label: String(relation.relationLabel).slice(0, 80),
      intentSummary: String(relation.intentSummary || relation.label || '').slice(0, 600),
      ...(relation.aboutPinId ? { aboutPinId: String(relation.aboutPinId) } : {}),
      ...(relation.aboutShapeId ? { aboutShapeId: String(relation.aboutShapeId) } : {}),
      ...(relation.aboutKind ? { aboutKind: String(relation.aboutKind) } : {}),
      fromId: String(relation.fromId),
      toId: String(relation.toId),
      ...(relation.fromKind ? { fromKind: String(relation.fromKind) } : {}),
      ...(relation.toKind ? { toKind: String(relation.toKind) } : {}),
      ...(relation.fromLabel ? { fromLabel: String(relation.fromLabel).slice(0, 80) } : {}),
      ...(relation.toLabel ? { toLabel: String(relation.toLabel).slice(0, 80) } : {}),
      relationLabel: String(relation.relationLabel).slice(0, 80),
      source: 'connecting-line',
      at,
    });
  }

  return events;
}

export function buildDigestDeleteEvents({
  activityIds = [],
  shapeIds = [],
  labels = {},
} = {}) {
  const at = Date.now();
  const events = [];
  for (const id of activityIds) {
    const pinId = String(id || '').trim();
    if (!pinId) continue;
    events.push({
      op: 'delete',
      pinId,
      label: String(labels[pinId] || pinId).slice(0, 80),
      intentSummary: '',
      at,
    });
  }
  for (const id of shapeIds) {
    const shapeId = String(id || '').trim();
    if (!shapeId) continue;
    events.push({
      op: 'delete',
      shapeId,
      label: String(labels[shapeId] || shapeId).slice(0, 80),
      intentSummary: '',
      at,
    });
  }
  return events;
}

export function boardNeedsStepsConstraintsPatch(board) {
  const recs = board?.contextRecommendations || {};
  const digest = recs.pendingDigest || {};
  if (digest.needsStepRefresh) return true;
  if (digest.userProgressDirty) return true;
  if (Array.isArray(digest.events) && digest.events.length) return true;
  return false;
}

/**
 * Store listeners skip per-shape digest-delete while a bulk canvas delete
 * batches pin + evidence events into one `/digest-event` POST (revoke + coalesce).
 */
export const digestDeleteListenersSuppressed = { active: false };

export async function withDigestDeleteListenersSuppressed(fn) {
  digestDeleteListenersSuppressed.active = true;
  try {
    return await fn();
  } finally {
    digestDeleteListenersSuppressed.active = false;
  }
}

function applyDigestResult(tag, result) {
  if (!result?.ok) return;
  if (useBoardStore.getState().hashtag !== tag) return;
  const store = useBoardStore.getState();
  const prior = store.board?.contextRecommendations || {};
  store.updateContextRecommendationsLocal({
    ...prior,
    constraints: result.constraints ?? prior.constraints,
    pendingDigest: result.pendingDigest ?? prior.pendingDigest,
    ...(result.userProgress ? { userProgress: result.userProgress } : {}),
  });
}

/** Fire-and-forget digest add/delete from non-React call sites. */
export function enqueueDigestEventsFromStore(events = []) {
  const store = useBoardStore.getState();
  const tag = store.hashtag;
  const list = (Array.isArray(events) ? events : [events])
    .filter(e => e && (e.op === 'add' || e.op === 'delete'));
  if (!tag || !list.length) return;
  postBoardAiDigestEvent(tag, { events: list })
    .then(result => applyDigestResult(tag, result))
    .catch((err) => {
      console.warn('[digest-event] enqueue failed', err?.message || err);
    });
}

/** Fire-and-forget digest add (relations / cluster claims) from non-React call sites. */
export function enqueueDigestAddFromStore(events = []) {
  enqueueDigestEventsFromStore(
    (Array.isArray(events) ? events : [events]).filter(e => e && e.op === 'add')
  );
}

/** Fire-and-forget digest delete from non-React call sites. */
export function enqueueDigestDeleteFromStore(activityIds = [], shapeIds = []) {
  const store = useBoardStore.getState();
  const tag = store.hashtag;
  const events = buildDigestDeleteEvents({ activityIds, shapeIds });
  if (!tag || !events.length) return;

  const prior = store.board?.contextRecommendations || {};
  const pruned = pruneFillsForDeletedSources(prior.userProgress, { activityIds, shapeIds });
  store.updateContextRecommendationsLocal({
    ...prior,
    constraints: withdrawConstraintsForDeletedSources(prior.constraints, { activityIds, shapeIds }),
    pendingDigest: markDigestDeleted(prior.pendingDigest),
    ...(pruned.pruned ? { userProgress: pruned.userProgress } : {}),
  });

  postBoardAiDigestEvent(tag, { events })
    .then(result => applyDigestResult(tag, result))
    .catch((err) => {
      console.warn('[digest-event] delete enqueue failed', err?.message || err);
    });
}
